import {
  addDays,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns';
import type { Locale } from 'date-fns';

// Pure date helpers for month-granularity range selection.
// All returned dates are local dates at start of day.

export type CompareMode = 'previous_period' | 'last_year';

export type DateRange = { start: Date; end: Date };

export type MonthRangeValue = {
  start: Date;
  end: Date;
  compare: boolean;
  compareMode: CompareMode;
};

/**
 * Dates are local start-of-day. Serialize with `getDateRangeIso` (src/utils/format-time.ts) or
 * `fDate(date, 'yyyy-MM-dd')`, never `toISOString()` (a UTC conversion shifts the 1st of the month
 * to the previous day in UTC+1/+2).
 */
export type MonthRangeApplied = MonthRangeValue & {
  compareStart: Date | null;
  compareEnd: Date | null;
};

const lastDayOfMonth = (date: Date): Date => startOfDay(endOfMonth(date));

/** Latest selectable day: yesterday (today's data is not complete yet). */
export const defaultMaxDate = (today: Date = new Date()): Date => startOfDay(subDays(today, 1));

/** Month-aligned range covering the months of `a` and `b` (any order), end clamped to maxDate. */
export function monthBounds(a: Date, b: Date, maxDate: Date): DateRange {
  const [from, to] = isAfter(startOfMonth(a), startOfMonth(b)) ? [b, a] : [a, b];
  const monthEnd = lastDayOfMonth(to);
  const max = startOfDay(maxDate);
  return { start: startOfMonth(from), end: isAfter(monthEnd, max) ? max : monthEnd };
}

/** Number of calendar months covered by the range, inclusive. */
export const monthCount = ({ start, end }: DateRange): number => differenceInCalendarMonths(end, start) + 1;

// Shifts a month-aligned range back by `months`. A partial last month (clamped to maxDate)
// stays partial so both periods cover the same days of month.
function shiftBack({ start, end }: DateRange, months: number): DateRange {
  const isFullLastMonth = isSameDay(end, lastDayOfMonth(end));
  return {
    start: startOfMonth(subMonths(start, months)),
    end: isFullLastMonth ? lastDayOfMonth(subMonths(end, months)) : startOfDay(subMonths(end, months)),
  };
}

/** Same number of months, immediately before the range. */
export const previousPeriod = (range: DateRange): DateRange => shiftBack(range, monthCount(range));

/** Same months, one year earlier. */
export const lastYear = (range: DateRange): DateRange => shiftBack(range, 12);

export function compareRange(value: MonthRangeValue): DateRange | null {
  if (!value.compare) return null;
  const range = { start: value.start, end: value.end };
  return value.compareMode === 'last_year' ? lastYear(range) : previousPeriod(range);
}

// Presets are relative to "today" = the day after maxDate.

/** Current month up to maxDate; on the 1st of a month (no data yet) falls back to maxDate's month. */
export function thisMonth(maxDate: Date): DateRange {
  const today = addDays(startOfDay(maxDate), 1);
  const month = isAfter(startOfMonth(today), startOfDay(maxDate)) ? maxDate : today;
  return monthBounds(month, month, maxDate);
}

/** Full month before today's month. */
export function lastMonth(maxDate: Date): DateRange {
  const month = subMonths(startOfMonth(addDays(startOfDay(maxDate), 1)), 1);
  return monthBounds(month, month, maxDate);
}

/** The 6 full months before today's month (the last one is the Last month preset). */
export function last6Months(maxDate: Date): DateRange {
  const { start: end } = lastMonth(maxDate);
  return monthBounds(subMonths(end, 5), end, maxDate);
}

/** 1st of maxDate's quarter up to maxDate. */
export const thisQuarter = (maxDate: Date): DateRange => monthBounds(startOfQuarter(maxDate), maxDate, maxDate);

/** January 1st of maxDate's year up to maxDate. */
export const thisYear = (maxDate: Date): DateRange => monthBounds(startOfYear(maxDate), maxDate, maxDate);

export function isMonthDisabled(month: Date, maxDate: Date, minDate?: Date): boolean {
  if (isAfter(startOfMonth(month), startOfDay(maxDate))) return true;
  return !!minDate && isBefore(lastDayOfMonth(month), startOfDay(minDate));
}

export function isMonthInRange(month: Date, range: DateRange | null): boolean {
  if (!range) return false;
  const m = startOfMonth(month);
  return !isBefore(m, startOfMonth(range.start)) && !isAfter(m, startOfMonth(range.end));
}

export const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** "January 2026", "Feb – Apr 2026" or "Nov 2025 – Jan 2026". */
export function formatMonthRangeLabel(start: Date, end: Date, locale?: Locale): string {
  const fmt = (date: Date, pattern: string) => capitalize(format(date, pattern, { locale }));
  if (differenceInCalendarMonths(end, start) === 0) return fmt(start, 'MMMM yyyy');
  if (start.getFullYear() === end.getFullYear()) return `${fmt(start, 'MMM')} – ${fmt(end, 'MMM yyyy')}`;
  return `${fmt(start, 'MMM yyyy')} – ${fmt(end, 'MMM yyyy')}`;
}
